// The Operators view, inside the Performance tab.
//
// Reads top to bottom the way the question is actually asked:
//
//   1. What is outstanding. Who has no instruction, and which declared numbers
//      nothing measures. This is deliberately FIRST and not buried at the
//      bottom, because on the day this ships it is the whole of the truth, and a
//      screen that opens on a handful of measured tiles while eight people have
//      no instruction is flattering rather than useful.
//   2. One card per person, their primary number and their supporting numbers.
//   3. The series behind the numbers, at day, week or month.
//
// Two rules from this repository apply hard here:
//   - A missing figure renders as missing, never as zero.
//   - loading means "not known yet", never "being re-checked". This component
//     never unmounts itself on a refetch.
import { useCallback, useEffect, useMemo, useState } from 'react'
import { AlertTriangle, ClipboardPaste, RefreshCw, ShieldQuestion } from 'lucide-react'
import { Spinner } from '../../lib/shadcn/spinner'
import { Button } from '../../lib/shadcn/button'
import {
  useGetOperatorBoard,
  useGetOperatorSeries,
  useRefreshOperatorMeasures,
} from '../../hooks/backend/operator'
import {
  byPerson,
  formatPeriod,
  formatTarget,
  formatValue,
  formatWhen,
  slotLabel,
  stateClass,
  targetVerdict,
  verdictClass,
  NOT_MEASURED,
  type Grain,
  type OperatorBoard,
  type ScoreboardRow,
  type SeriesRow,
} from '../../utils/operator'
import { FilterBar } from '../common/FilterBar'
import { PasteInstructionPanel } from './PasteInstructionPanel'
import { NumberSeries } from './NumberSeries'
import { BindNumber } from './BindNumber'

const EMPTY: OperatorBoard = {
  scoreboard: [],
  instructions: [],
  missing: [],
  numbers: [],
  measures: [],
  last_computed_at: null,
}

const GRAINS: Grain[] = ['day', 'week', 'month']

function Tile({ row }: { row: ScoreboardRow }) {
  const v = targetVerdict(row)
  const measured = row.latest_value !== null
  return (
    <div className="rounded-lg border border-border bg-background p-3">
      <div className="flex items-baseline justify-between gap-2">
        <span className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
          {slotLabel(row.slot)}
        </span>
        {row.latest_is_partial ? (
          <span className="rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-medium text-amber-900 dark:bg-amber-950 dark:text-amber-200">
            part period
          </span>
        ) : null}
      </div>
      <div className="mt-0.5 text-sm font-medium text-foreground">{row.name}</div>
      <div
        className={`mt-1 text-xl font-semibold tabular-nums ${
          measured ? verdictClass(v) : 'text-muted-foreground'
        }`}
      >
        {measured ? formatValue(row.latest_value, row.unit) : NOT_MEASURED}
      </div>
      <div className="mt-1 space-y-0.5 text-xs text-muted-foreground">
        <div>Target: {formatTarget(row)}</div>
        {measured && row.latest_period ? (
          <div>
            {formatPeriod(row.latest_period, row.latest_grain ?? 'month')}
            {row.latest_grain ? ` (${row.latest_grain})` : ''}
          </div>
        ) : (
          <div className={stateClass(row.number_state)}>{row.number_state}</div>
        )}
        {row.declared_status ? (
          <div className="italic">Their own note: {row.declared_status}</div>
        ) : null}
      </div>
    </div>
  )
}

export function OperatorsView({ isAdmin }: { isAdmin: boolean }) {
  const boardFn = useGetOperatorBoard()
  const seriesFn = useGetOperatorSeries()
  const refreshFn = useRefreshOperatorMeasures()

  const [grain, setGrain] = useState<Grain>('month')
  const [pasteFor, setPasteFor] = useState<{ id: string; name: string } | null>(null)
  const [refreshNote, setRefreshNote] = useState<string | null>(null)
  // The same three filters the Performance tab offers, in the same order: a day
  // range, a person, and the division they sit in.
  const [from, setFrom] = useState('')
  const [to, setTo] = useState('')
  const [personId, setPersonId] = useState('')
  const [division, setDivision] = useState('')

  const triggerBoard = boardFn.trigger
  const triggerSeries = seriesFn.trigger

  const loadBoard = useCallback(() => {
    triggerBoard({}, { skipCache: true })
  }, [triggerBoard])

  useEffect(() => {
    loadBoard()
  }, [loadBoard])

  // getOperatorSeries already accepts person_id, from and to. Until now this
  // screen passed only the grain, so the filters below need no new endpoint.
  useEffect(() => {
    triggerSeries(
      {
        grain,
        person_id: personId || null,
        from: from || null,
        to: to || null,
      },
      { skipCache: true },
    )
  }, [triggerSeries, grain, personId, from, to])

  const board = (boardFn.data as OperatorBoard | undefined) ?? EMPTY
  const series = ((seriesFn.data as { rows: SeriesRow[] } | undefined)?.rows ?? []) as SeriesRow[]

  // Every person and every division on the board, for the two menus. Built from
  // the unfiltered board so narrowing the filter never empties the menu you
  // would need to widen it again.
  const allPeople = useMemo(() => {
    const m = new Map<string, string>()
    for (const r of board.scoreboard) m.set(r.person_id, r.person_name)
    for (const r of board.missing) if (r.person_id) m.set(r.person_id, r.person_name || r.email)
    return Array.from(m, ([id, name]) => ({ id, name })).sort((a, b) => a.name.localeCompare(b.name))
  }, [board.scoreboard, board.missing])

  const allDivisions = useMemo(() => {
    const set = new Set<string>()
    for (const r of board.scoreboard) set.add(r.division || 'no_role')
    return Array.from(set).sort()
  }, [board.scoreboard])

  const filteredScoreboard = useMemo(
    () =>
      board.scoreboard.filter((r) => {
        if (personId && r.person_id !== personId) return false
        if (division && (r.division || 'no_role') !== division) return false
        return true
      }),
    [board.scoreboard, personId, division],
  )

  const people = useMemo(() => byPerson(filteredScoreboard), [filteredScoreboard])
  // Which division each person sits in, taken off the scoreboard so the two
  // lists below can honour the same filter without a second lookup.
  const divisionByPerson = useMemo(() => {
    const m = new Map<string, string>()
    for (const r of board.scoreboard) m.set(r.person_id, r.division || 'no_role')
    return m
  }, [board.scoreboard])

  const inFilter = useCallback(
    (id: string | null) => {
      if (personId && id !== personId) return false
      if (division && (id === null || (divisionByPerson.get(id) ?? 'no_role') !== division)) {
        return false
      }
      return true
    },
    [personId, division, divisionByPerson],
  )

  const unmeasured = useMemo(
    () => board.numbers.filter((n) => n.number_state !== 'measured' && inFilter(n.person_id)),
    [board.numbers, inFilter],
  )

  const missing = useMemo(() => board.missing.filter((m) => inFilter(m.person_id)), [
    board.missing,
    inFilter,
  ])

  const onRefresh = async () => {
    // Three whole months at month grain. The function widens any window to whole
    // buckets, so the dates here are the intent and the result says what was
    // actually rebuilt.
    const to = new Date()
    const from = new Date(Date.UTC(to.getUTCFullYear(), to.getUTCMonth() - 2, 1))
    const iso = (d: Date) => d.toISOString().slice(0, 10)
    const res = (await refreshFn.trigger(
      { grain, from: iso(from), to: iso(to) },
      { skipCache: true },
    )) as unknown as Record<string, unknown> | undefined
    const payload = (res ?? refreshFn.data) as Record<string, unknown> | undefined
    if (payload && payload['ok'] === false) {
      setRefreshNote(String(payload['reason'] ?? 'The figures were not recomputed.'))
    } else if (payload) {
      // Every field is reported as the server sent it, and an absent count reads
      // as "not reported" rather than as zero. A refresh that wrote nothing and a
      // refresh that did not say are different things.
      const say = (k: string) => {
        const v = payload[k]
        return v === null || v === undefined ? 'not reported' : String(v)
      }
      setRefreshNote(
        `Asked for ${say('asked_from')} to ${say('asked_to')}. Rebuilt ${say(
          'rebuilt_from',
        )} to ${say('rebuilt_to')} at ${say('grain')} rhythm, because the whole period is rebuilt or none of it is. ${say(
          'rows_written',
        )} figures written, ${say('rows_cleared')} cleared first.`,
      )
    }
    loadBoard()
    triggerSeries({ grain }, { skipCache: true })
  }

  return (
    <div className="space-y-4">
      {/* Controls. One button on a phone; unchanged at sm and above. */}
      <FilterBar
        className="rounded-lg border border-border"
        activeCount={(from ? 1 : 0) + (to ? 1 : 0) + (personId ? 1 : 0) + (division ? 1 : 0)}
        summary={[
          grain,
          from || to ? `${from || 'start'} to ${to || 'today'}` : null,
          personId ? allPeople.find((p) => p.id === personId)?.name : null,
          division ? (division === 'no_role' ? 'No role recorded' : division.replace(/_/g, ' ')) : null,
        ]
          .filter(Boolean)
          .join(', ')}
        trailing={`${people.length} people`}
      >
      <div className="flex flex-wrap items-end gap-3 px-3 py-2">
        <div className="space-y-1">
          <span className="block text-[11px] text-muted-foreground">Rhythm</span>
          <div className="flex items-center gap-1">
            {GRAINS.map((g) => (
              <button
                key={g}
                type="button"
                onClick={() => setGrain(g)}
                className={`rounded-md px-2.5 py-1 text-xs font-medium capitalize transition-colors ${
                  grain === g
                    ? 'bg-[hsl(var(--brand-soft))] text-[hsl(var(--brand-soft-foreground))]'
                    : 'text-muted-foreground hover:bg-muted'
                }`}
              >
                {g}
              </button>
            ))}
          </div>
        </div>

        <label className="space-y-1">
          <span className="block text-[11px] text-muted-foreground">From</span>
          <input
            type="date"
            value={from}
            onChange={(e) => setFrom(e.target.value)}
            className="h-8 rounded-md border border-border bg-background px-2 text-xs text-foreground"
          />
        </label>

        <label className="space-y-1">
          <span className="block text-[11px] text-muted-foreground">To</span>
          <input
            type="date"
            value={to}
            onChange={(e) => setTo(e.target.value)}
            className="h-8 rounded-md border border-border bg-background px-2 text-xs text-foreground"
          />
        </label>

        <label className="space-y-1">
          <span className="block text-[11px] text-muted-foreground">Person</span>
          <select
            value={personId}
            onChange={(e) => setPersonId(e.target.value)}
            className="h-8 rounded-md border border-border bg-background px-2 text-xs text-foreground"
          >
            <option value="">Everyone</option>
            {allPeople.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
              </option>
            ))}
          </select>
        </label>

        <label className="space-y-1">
          <span className="block text-[11px] text-muted-foreground">Division</span>
          <select
            value={division}
            onChange={(e) => setDivision(e.target.value)}
            className="h-8 rounded-md border border-border bg-background px-2 text-xs text-foreground"
            title="Read off servant_roles. 'no role' is a real answer, not a default: it means nothing on record says what that person is."
          >
            <option value="">All divisions</option>
            {allDivisions.map((d) => (
              <option key={d} value={d}>
                {d === 'no_role' ? 'No role recorded' : d.replace(/_/g, ' ')}
              </option>
            ))}
          </select>
        </label>

        {(from || to || personId || division) && (
          <Button
            size="sm"
            variant="ghost"
            onClick={() => {
              setFrom('')
              setTo('')
              setPersonId('')
              setDivision('')
            }}
          >
            Clear
          </Button>
        )}
        <div className="ml-auto flex items-center gap-2 text-xs text-muted-foreground">
          <span>Figures last recomputed: {formatWhen(board.last_computed_at)}</span>
          {boardFn.loading ? <Spinner className="h-3.5 w-3.5" /> : null}
          {isAdmin ? (
            <Button size="sm" variant="outline" onClick={onRefresh} disabled={refreshFn.loading}>
              <RefreshCw className="mr-1.5 h-3.5 w-3.5" />
              Recompute
            </Button>
          ) : null}
        </div>
      </div>
      </FilterBar>

      {board.last_computed_at === null ? (
        <div className="rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900 dark:border-amber-900 dark:bg-amber-950 dark:text-amber-100">
          Nothing has been computed yet, so every number below reads as not measured. That is the
          real state, not a fault. Nothing recomputes these figures on a schedule; somebody has to
          decide which rhythms are refreshed and how often.
        </div>
      ) : null}

      {refreshNote ? (
        <div className="rounded-lg border border-border bg-card px-4 py-2 text-xs text-muted-foreground">
          {refreshNote}
        </div>
      ) : null}

      {boardFn.error ? (
        <div className="rounded-lg border border-red-300 bg-red-50 px-4 py-3 text-sm text-red-800 dark:border-red-900 dark:bg-red-950 dark:text-red-200">
          Failed to load the operator instructions: {boardFn.error}
        </div>
      ) : null}

      {/* 1. What is outstanding. First, on purpose. */}
      <section className="rounded-lg border border-border bg-card">
        <header className="flex items-center gap-2 border-b border-border px-4 py-2.5">
          <AlertTriangle className="h-4 w-4 text-amber-600 dark:text-amber-400" />
          <h2 className="text-sm font-semibold text-foreground">What is outstanding</h2>
        </header>

        <div className="grid gap-4 p-4 md:grid-cols-2">
          <div>
            <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              No instruction on record ({missing.length})
            </h3>
            {missing.length === 0 ? (
              <p className="mt-2 text-sm text-muted-foreground">
                {personId || division
                  ? 'Nobody in this filter is missing an instruction. Clear the filter to see the whole picture.'
                  : 'Everybody with a cockpit login holds a current instruction.'}
              </p>
            ) : (
              <ul className="mt-2 space-y-1.5">
                {missing.map((m) => (
                  <li
                    key={m.email}
                    className="flex items-center justify-between gap-2 rounded-md border border-border px-2.5 py-1.5"
                  >
                    <div className="min-w-0">
                      <div className="truncate text-sm font-medium text-foreground">
                        {m.person_name || m.email}
                      </div>
                      <div className="truncate text-xs text-muted-foreground">
                        {m.active_roles ?? 'no role recorded'} · {m.instruction_state}
                      </div>
                    </div>
                    {m.person_id ? (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() =>
                          setPasteFor({ id: m.person_id as string, name: m.person_name || m.email })
                        }
                      >
                        <ClipboardPaste className="mr-1.5 h-3.5 w-3.5" />
                        Paste
                      </Button>
                    ) : (
                      <span className="text-xs text-muted-foreground">no person record</span>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div>
            <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              Declared but not measured ({unmeasured.length})
            </h3>
            {unmeasured.length === 0 ? (
              <p className="mt-2 text-sm text-muted-foreground">
                Every declared number has a measure behind it with values in it.
              </p>
            ) : (
              <ul className="mt-2 space-y-1.5">
                {unmeasured.map((n) => (
                  <li key={n.metric_id} className="rounded-md border border-border px-2.5 py-1.5">
                    <div className="flex items-baseline justify-between gap-2">
                      <span className="text-sm font-medium text-foreground">{n.name}</span>
                      <span className={`text-xs ${stateClass(n.number_state)}`}>
                        {n.number_state}
                      </span>
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {n.person_name} · {slotLabel(n.slot)}
                      {n.cadence ? ` · checked ${n.cadence}` : ' · no rhythm stated'}
                    </div>
                    {/* This is the step that moves a number off this list. It was
                        missing: the handler existed and nothing called it, so the
                        screen named the gap and gave no way to close it. */}
                    <BindNumber
                      number={n}
                      measures={board.measures}
                      isAdmin={isAdmin}
                      onDone={loadBoard}
                    />
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </section>

      {/* 2. One card per person. */}
      {people.length === 0 ? (
        <div className="flex flex-col items-center gap-2 rounded-lg border border-border bg-card px-6 py-10 text-center">
          <ShieldQuestion className="h-6 w-6 text-muted-foreground" />
          <p className="text-sm font-medium text-foreground">
            No instruction has been pasted yet, so there is nobody to show.
          </p>
          <p className="max-w-md text-xs text-muted-foreground">
            Paste a document from the outstanding list above. Nothing is saved until the parse has
            been read back and confirmed.
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {people.map((p) => (
            <section key={p.person_id} className="rounded-lg border border-border bg-card">
              <header className="flex flex-wrap items-center gap-2 border-b border-border px-4 py-2.5">
                <h2 className="text-sm font-semibold text-foreground">{p.person_name}</h2>
                <span className="text-xs text-muted-foreground">
                  instruction v{p.instruction_version}
                </span>
                <div className="ml-auto">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setPasteFor({ id: p.person_id, name: p.person_name })}
                  >
                    <ClipboardPaste className="mr-1.5 h-3.5 w-3.5" />
                    Replace instruction
                  </Button>
                </div>
              </header>
              <div className="grid gap-3 p-4 sm:grid-cols-2 lg:grid-cols-3">
                {p.primary ? (
                  <Tile row={p.primary} />
                ) : (
                  <div className="rounded-lg border border-dashed border-border p-3 text-xs text-muted-foreground">
                    No primary number on this instruction.
                  </div>
                )}
                {p.others.map((row) => (
                  <Tile key={row.metric_id} row={row} />
                ))}
              </div>
              <NumberSeries
                rows={series.filter((s) => s.person_id === p.person_id)}
                grain={grain}
                loading={seriesFn.loading}
              />
            </section>
          ))}
        </div>
      )}

      {/* 3. Fleet numbers, which belong to nobody in particular. */}
      {series.some((s) => s.person_id === null) ? (
        <section className="rounded-lg border border-border bg-card">
          <header className="border-b border-border px-4 py-2.5">
            <h2 className="text-sm font-semibold text-foreground">Fleet numbers</h2>
            <p className="text-xs text-muted-foreground">
              Bound to somebody&apos;s instruction but measured across the whole fleet.
            </p>
          </header>
          <NumberSeries
            rows={series.filter((s) => s.person_id === null)}
            grain={grain}
            loading={seriesFn.loading}
          />
        </section>
      ) : null}

      {pasteFor ? (
        <PasteInstructionPanel
          personId={pasteFor.id}
          personName={pasteFor.name}
          measures={board.measures}
          onClose={() => setPasteFor(null)}
          onSaved={() => {
            setPasteFor(null)
            loadBoard()
          }}
        />
      ) : null}
    </div>
  )
}
